import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  attemptSilentReauth,
  forgetSession,
  isProtectedPath,
  markSessionActive,
  rememberProvider,
} from '@/lib/session';

export interface User {
  id: string;
  name: string;
  email: string;
  avatarUrl?: string;
  provider: 'github' | 'aad' | 'twitter' | 'google' | 'facebook' | 'demo';
  roles: string[];
}

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login: (provider?: string) => void;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// Azure Static Web Apps provides /.auth/me to get current user
async function fetchAzureUser(): Promise<User | null> {
  try {
    const res = await fetch('/.auth/me');
    if (!res.ok) return null;
    const data = await res.json();
    const principal = data?.clientPrincipal;
    if (!principal) return null;
    return {
      id: principal.userId,
      name: principal.userDetails,
      email: principal.userDetails,
      provider: principal.identityProvider,
      roles: principal.userRoles ?? [],
    };
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // In development / demo mode, use a mock user
    const isDev = import.meta.env.DEV;
    if (isDev) {
      const stored = localStorage.getItem('demo_user');
      if (stored) {
        setUser(JSON.parse(stored));
      }
      setLoading(false);
      return;
    }

    fetchAzureUser().then(u => {
      if (u) {
        rememberProvider(u.provider);
        markSessionActive();
        setUser(u);
        setLoading(false);
        return;
      }

      // No SWA session. The cookie only lasts ~8 hours, so for a returning
      // visitor this usually means it simply lapsed rather than that they signed
      // out. Bounce them through their provider to get a fresh one — normally
      // silent. Only worth doing where they'd otherwise hit a login wall.
      const shouldReauth = isProtectedPath() || window.location.pathname === '/login';
      if (shouldReauth && attemptSilentReauth()) return; // navigating away; stay in loading

      setUser(null);
      setLoading(false);
    });
  }, []);

  const login = useCallback((provider = 'github') => {
    if (import.meta.env.DEV) {
      // Demo login
      const demoUser: User = {
        id: 'demo-user-1',
        name: 'Jane Gardener',
        email: 'jane@example.com',
        provider: 'demo',
        roles: ['authenticated'],
      };
      localStorage.setItem('demo_user', JSON.stringify(demoUser));
      setUser(demoUser);
      return;
    }
    rememberProvider(provider);
    window.location.href = `/.auth/login/${provider}?post_login_redirect_uri=/dashboard`;
  }, []);

  const logout = useCallback(() => {
    // Must come first: leaving the remembered provider in place would let the
    // silent re-auth bounce sign them straight back in.
    forgetSession();
    if (import.meta.env.DEV) {
      localStorage.removeItem('demo_user');
      setUser(null);
      return;
    }
    window.location.href = '/.auth/logout?post_logout_redirect_uri=/';
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
